import { Alert, Button, Group, Modal, Progress, Text } from '@mantine/core';
import {
  IconAlertCircle,
  IconDownload,
  IconFileTypeDocx,
  IconPlayerPause,
  IconPlayerPlay,
  IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { fileSizeText } from '../forms/status.js';
import { CardFooter, Fact, Facts } from './SettingsLayout.js';

const ICON = { size: 18, stroke: 1.8 } as const;
/** Bildirim okunmadan önce gösterilen yaklaşık indirme boyutu (deneme: ~140 MB). */
export const OFFICE_PACK_APPROX_BYTES = 140 * 1024 * 1024;

/** Ana sürecin Felixmate Office Pack durumu (apps/desktop `office-pack/service.ts`; ADR 0070). */
export interface OfficePackView {
  enabled: boolean;
  installed: { version: string; libreOfficeVersion: string; unpackedSize: number } | null;
  available: {
    version: string;
    libreOfficeVersion: string;
    unpackedSize: number;
    size: number;
  } | null;
  job:
    | { state: 'idle' }
    | { state: 'waiting' }
    | { state: 'downloading'; received: number; total: number }
    | { state: 'paused'; received: number; total: number }
    | { state: 'installing' }
    | { state: 'error'; code: string };
  ask: { firstRun: boolean; fillIn: boolean };
}

/**
 * "Felixmate Office Pack indirilsin mi?" (ADR 0070 §9): ilk açılışta ve ilk "Fill in"de. Varsayılan
 * cevap İndir (odak onda). "Sonra" denirse o soru bir daha sorulmaz.
 */
export function OfficePackOfferModal({
  opened,
  size,
  variant,
  onAnswer,
}: {
  opened: boolean;
  /** İndirme boyutu (bayt); bilinmiyorsa yaklaşık değer yazılır. */
  size: number | null;
  variant: 'firstRun' | 'fillIn';
  onAnswer: (download: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const sizeText = size
    ? fileSizeText(size, locale)
    : t('officePack.offer.approximate', {
        size: fileSizeText(OFFICE_PACK_APPROX_BYTES, locale),
      });
  return (
    <Modal
      opened={opened}
      onClose={() => onAnswer(false)}
      title={t('officePack.offer.title')}
      closeButtonProps={{ 'aria-label': t('officePack.offer.close') }}
      data-office-pack-offer={variant}
    >
      <Text size="sm">{t('officePack.offer.body', { size: sizeText })}</Text>
      {variant === 'fillIn' ? (
        <Text size="sm" mt="sm">
          {t('officePack.offer.fillIn')}
        </Text>
      ) : null}
      <Group justify="flex-end" mt="lg">
        <Button variant="default" onClick={() => onAnswer(false)}>
          {t('officePack.offer.later')}
        </Button>
        <Button
          leftSection={<IconDownload size={16} />}
          onClick={() => onAnswer(true)}
          data-autofocus
        >
          {t('officePack.offer.download')}
        </Button>
      </Group>
    </Modal>
  );
}

function badge(view: OfficePackView): {
  tone: StatusTone;
  key: string;
  values?: Record<string, string | number>;
} {
  const job = view.job;
  if (!view.enabled && !view.installed) return { tone: 'neutral', key: 'unavailable' };
  if (job.state === 'downloading') {
    return {
      tone: 'info',
      key: 'downloading',
      values: { percent: job.total ? Math.floor((job.received / job.total) * 100) : 0 },
    };
  }
  if (job.state === 'installing') return { tone: 'info', key: 'installing' };
  if (job.state === 'paused') return { tone: 'neutral', key: 'paused' };
  if (job.state === 'waiting') return { tone: 'warning', key: 'waiting' };
  if (job.state === 'error') return { tone: 'danger', key: 'error' };
  if (view.installed && view.available) {
    return { tone: 'info', key: 'update', values: { version: view.available.version } };
  }
  if (view.installed) return { tone: 'success', key: 'installed' };
  return { tone: 'neutral', key: 'notInstalled' };
}

/**
 * Ayarlar ▸ "Felixmate Office Pack" (ADR 0070 §9): durum, sürüm, boyut, ilerleme; İndir/Sürdür,
 * Duraklat ve Kaldır. Kaldırma onay ister; açık form varken ana süreç reddeder (`IN_USE`).
 */
export function OfficePackCard({
  view,
  onDownload,
  onPause,
  onRemove,
}: {
  view: OfficePackView;
  onDownload: () => void;
  onPause: () => void;
  onRemove: () => Promise<{ ok: true } | { ok: false; code: string }>;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const [confirm, setConfirm] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const size = (bytes: number) => fileSizeText(bytes, locale);
  const state = badge(view);
  const job = view.job;
  const running =
    job.state === 'downloading' || job.state === 'installing' || job.state === 'waiting';
  const pack = view.available ?? view.installed;
  const errorText =
    job.state === 'error'
      ? t(`officePack.card.errors.${job.code}`, {
          defaultValue: t('officePack.card.errors.generic'),
        })
      : null;
  const canDownload =
    view.enabled && !running && (view.installed === null || view.available !== null);

  return (
    <Panel
      title={t('officePack.card.title')}
      icon={<IconFileTypeDocx {...ICON} />}
      divider
      extra={
        <StatusBadge tone={state.tone} withIcon>
          {t(`officePack.card.states.${state.key}`, state.values ?? {})}
        </StatusBadge>
      }
    >
      <Facts compact>
        <Fact label={t('officePack.card.product')} help={t('officePack.card.help')}>
          {t('officePack.card.productValue')}
        </Fact>
        <Fact label={t('officePack.card.version')}>
          {pack ? t('officePack.card.versionValue', { version: pack.version }) : '—'}
        </Fact>
        {pack ? (
          <Fact label={t('officePack.card.licence')}>
            {t('officePack.card.licenceValue', { libreOffice: pack.libreOfficeVersion })}
          </Fact>
        ) : null}
        <Fact label={t('officePack.card.size')}>
          {view.available
            ? t('officePack.card.sizeValue', {
                download: size(view.available.size),
                disk: size(view.available.unpackedSize),
              })
            : view.installed
              ? t('officePack.card.diskValue', { disk: size(view.installed.unpackedSize) })
              : '—'}
        </Fact>
        {job.state === 'downloading' || job.state === 'paused' ? (
          <Fact label={t('officePack.card.status')}>
            <Progress
              value={job.total ? (job.received / job.total) * 100 : 0}
              aria-label={t('officePack.card.title')}
              mb={4}
            />
            {job.total
              ? t('officePack.card.progress', {
                  received: size(job.received),
                  total: size(job.total),
                })
              : null}
          </Fact>
        ) : null}
      </Facts>
      {errorText || removeError ? (
        <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert" mt="sm">
          {removeError ?? errorText}
        </Alert>
      ) : null}
      {view.enabled || view.installed ? (
        <CardFooter>
          {view.installed && !running ? (
            <Button
              variant="default"
              leftSection={<IconTrash size={16} />}
              onClick={() => {
                setRemoveError(null);
                setConfirm(true);
              }}
            >
              {t('officePack.card.remove')}
            </Button>
          ) : null}
          {job.state === 'downloading' || job.state === 'waiting' ? (
            <Button variant="default" leftSection={<IconPlayerPause size={16} />} onClick={onPause}>
              {t('officePack.card.pause')}
            </Button>
          ) : null}
          {canDownload ? (
            <Button
              leftSection={
                job.state === 'paused' ? <IconPlayerPlay size={16} /> : <IconDownload size={16} />
              }
              onClick={onDownload}
            >
              {t(job.state === 'paused' ? 'officePack.card.resume' : 'officePack.card.download')}
            </Button>
          ) : null}
        </CardFooter>
      ) : null}
      <Modal
        opened={confirm}
        onClose={() => setConfirm(false)}
        title={t('officePack.card.removeTitle')}
        closeButtonProps={{ 'aria-label': t('officePack.card.cancel') }}
      >
        <Text size="sm">{t('officePack.card.removeBody')}</Text>
        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setConfirm(false)} data-autofocus>
            {t('officePack.card.cancel')}
          </Button>
          <Button
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={() => {
              setConfirm(false);
              void onRemove().then((result) => {
                if (!result.ok) {
                  setRemoveError(
                    t(`officePack.card.errors.${result.code}`, {
                      defaultValue: t('officePack.card.errors.generic'),
                    }),
                  );
                }
              });
            }}
          >
            {t('officePack.card.removeConfirm')}
          </Button>
        </Group>
      </Modal>
    </Panel>
  );
}
